/**
 * Location Service
 * 
 * Handles CRUD operations for Location entities.
 */

import type {
  CommercialModule,
  CrewActivityVisibility,
  GuestFormField,
  Location,
  LocationInput,
  LocationContact,
  LocationFilters,
  LocationSetupReport,
  LocationUpdateInput,
  LocationSummary,
  LocationWithBillingProfiles,
  OperationalStatus,
  WeekStartDay,
  WeekendDayCode,
} from '../types';
import { apiClient } from '@/shared/services/api';
import { COUNTRIES } from '@/shared/constants/countries';

/**
 * Shape of a Location returned by the Django backend.
 * Keys are camelCase because apiClient applies transformKeys() on all responses.
 */
interface BackendLocation {
  id: string;
  name: string;
  type: string;
  description?: string;
  operationalStatus?: OperationalStatus;
  brandColor?: string;
  weekStartDay?: WeekStartDay;
  weekendDays?: WeekendDayCode[];
  // Display & formatting
  dateFormat?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat?: '24h' | '12h';
  numberFormat?: 'point' | 'comma' | 'space';
  unitSystem?: 'metric' | 'imperial';
  // Capacity & limits
  totalRooms?: number;
  totalBeds?: number;
  maxGuests?: number;
  maxPerRoom?: number;
  occupancyWarningPct?: number;
  occupancyCriticalPct?: number;
  blockOvercapacityBookings?: boolean;
  guestFormRequiredFields?: string[];
  allowExtraGuestBeds?: boolean;
  minStayNights?: number;
  maxStayNights?: number;
  defaultStayNights?: number;
  defaultCheckInTime?: string;
  defaultCheckOutTime?: string;
  bookingWindowDays?: number;
  maxPerBooking?: number;
  groupThreshold?: number;
  groupRequiresApproval?: boolean;
  // Monetary defaults
  showPricesInGuestCurrency?: boolean;
  taxLabel?: string;
  taxDisplayMode?: 'inclusive' | 'exclusive';
  cashRounding?: 'none' | 'nearest_05' | 'nearest_10';
  displayPrecision?: number;
  roundLineItemsIndividually?: boolean;
  tippingEnabled?: boolean;
  suggestedTips?: number[];
  // Guest payment defaults
  defaultDepositPct?: number;
  balanceDueWindow?: '14_days_before' | '7_days_before' | 'on_checkin';
  cancellationPolicyText?: string;
  folioChargeFnb?: boolean;
  folioChargeActivities?: boolean;
  folioChargeShop?: boolean;
  folioCapPerStay?: string | number;
  allowedPaymentMethods?: string[];
  prepaidMinRecharge?: string | number;
  prepaidMaxBalance?: string | number;
  prepaidDefaultTopups?: number[];
  prepaidRefundPolicy?: 'auto_on_checkout' | 'on_request' | 'forfeit';
  prepaidBonusTiers?: Array<{ threshold: number | string; bonus: number | string }>;
  prepaidRedemptionSurfaces?: string[];
  // Staff defaults
  minRestBetweenShiftsHours?: number;
  autoApproveShiftCoverage?: boolean;
  activityCancelAfterStartRequiresManager?: boolean;
  folioCapOverrideRequiresManager?: boolean;
  crewNfcAttendanceEnabled?: boolean;
  crewManualAttendanceEnabled?: boolean;
  crewActivityVisibility?: string;
  crewShiftSwapEnabled?: boolean;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phone: string;
  email: string;
  website: string;
  logoUrl?: string;
  contactConfig?: {
    phoneSecondary?: string;
    socialMedia?: {
      instagram?: string;
      facebook?: string;
      twitter?: string;
      whatsapp?: string;
      tiktok?: string;
      youtube?: string;
    };
    departmentContacts?: Record<string, {
      name?: string;
      email?: string;
      phone?: string;
    }>;
  };
  timezone: string;
  currency: string;
  currencies?: Array<{ code: string; symbol?: string; locale?: string; exchangeRate?: number; isDefault: boolean }>;
  locale: string;
  activeModules?: string[];
  isActive: boolean;
  guestRoomChargeEnabled?: boolean;
  guestPrepaidEnabled?: boolean;
  guestPaymentModeDefault?: 'room_charge' | 'prepaid';
  createdAt: string;
  updatedAt: string;
}

function normalizeCountryCode(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim();
  const byCode = COUNTRIES.find((country) => country.code === normalized.toUpperCase());
  if (byCode) return byCode.code;
  const byName = COUNTRIES.find((country) => country.nameEn.toLowerCase() === normalized.toLowerCase());
  return byName?.code;
}

function buildContactConfig(contact: Location['contact'] | undefined) {
  const phoneSecondary = contact?.phoneSecondary?.trim();
  const socialMedia = contact?.socialMedia || {};
  const departmentContacts = contact?.departmentContacts || {};

  return {
    phone_secondary: phoneSecondary || '',
    social_media: {
      instagram: socialMedia.instagram || '',
      facebook: socialMedia.facebook || '',
      twitter: socialMedia.twitter || '',
      whatsapp: socialMedia.whatsapp || '',
      tiktok: socialMedia.tiktok || '',
      youtube: socialMedia.youtube || '',
    },
    department_contacts: departmentContacts,
  };
}

/** Maps a backend Location to the frontend Location type */
function mapBackendLocation(b: BackendLocation): Location {
  const slug = b.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const hasContactData = Boolean(
    b.phone ||
    b.email ||
    b.website ||
    b.contactConfig?.phoneSecondary ||
    b.contactConfig?.socialMedia?.instagram ||
    b.contactConfig?.socialMedia?.facebook ||
    b.contactConfig?.socialMedia?.twitter ||
    b.contactConfig?.socialMedia?.whatsapp ||
    Object.keys(b.contactConfig?.departmentContacts || {}).length,
  );

  return {
    id: b.id,
    name: b.name,
    slug,
    type: (b.type as Location['type']) || 'surfcamp',
    status: b.isActive ? 'active' : 'inactive',
    operationalStatus: (b.operationalStatus ?? 'active') as OperationalStatus,
    brandColor: b.brandColor || undefined,
    description: b.description || undefined,
    weekStartDay: (b.weekStartDay ?? 'monday') as WeekStartDay,
    defaultCheckInTime: (b.defaultCheckInTime ?? '14:00:00').slice(0, 5),
    defaultCheckOutTime: (b.defaultCheckOutTime ?? '11:00:00').slice(0, 5),
    weekendDays: (b.weekendDays && b.weekendDays.length > 0
      ? b.weekendDays
      : ['sat', 'sun']) as WeekendDayCode[],
    dateFormat: b.dateFormat ?? 'DD/MM/YYYY',
    timeFormat: b.timeFormat ?? '24h',
    numberFormat: b.numberFormat ?? 'comma',
    // Not inside `capacity`: this is about guest FORMS, not about how
    // many people fit. Absent means name-only, the pre-setting behaviour.
    guestFormRequiredFields: (b.guestFormRequiredFields ?? []) as GuestFormField[],
    unitSystem: b.unitSystem ?? 'metric',
    capacity: {
      totalRooms: b.totalRooms ?? 0,
      totalBeds: b.totalBeds ?? 0,
      maxGuests: b.maxGuests ?? 0,
      maxPerRoom: b.maxPerRoom ?? 0,
      occupancyWarningPct: b.occupancyWarningPct ?? 85,
      occupancyCriticalPct: b.occupancyCriticalPct ?? 95,
      blockOvercapacityBookings: b.blockOvercapacityBookings ?? true,
      allowExtraGuestBeds: b.allowExtraGuestBeds ?? true,
      minStayNights: b.minStayNights ?? 1,
      maxStayNights: b.maxStayNights ?? 30,
      defaultStayNights: b.defaultStayNights ?? 7,
      bookingWindowDays: b.bookingWindowDays ?? 365,
      maxPerBooking: b.maxPerBooking ?? 12,
      groupThreshold: b.groupThreshold ?? 6,
      groupRequiresApproval: b.groupRequiresApproval ?? false,
    },
    activeModules: ((b.activeModules ?? ['core_stay']) as CommercialModule[]),
    locale: b.locale || 'en-US',
    address: {
      street: b.address || '',
      city: b.city,
      state: b.state || undefined,
      country: normalizeCountryCode(b.country) || b.country,
      postalCode: b.postalCode || undefined,
    },
    contact: hasContactData ? {
      phone: b.phone || undefined,
      phoneSecondary: b.contactConfig?.phoneSecondary || undefined,
      email: b.email || undefined,
      website: b.website || undefined,
      socialMedia: b.contactConfig?.socialMedia,
      departmentContacts: b.contactConfig?.departmentContacts as LocationContact['departmentContacts'],
    } : undefined,
    logoUrl: b.logoUrl || undefined,
    monetary: {
      currencies: b.currencies && b.currencies.length > 0
        ? b.currencies
        : [{ code: b.currency || 'EUR', isDefault: true }],
      timezone: b.timezone || 'UTC',
      showPricesInGuestCurrency: b.showPricesInGuestCurrency ?? false,
      taxLabel: b.taxLabel ?? 'VAT',
      taxDisplayMode: b.taxDisplayMode ?? 'inclusive',
      cashRounding: b.cashRounding ?? 'none',
      displayPrecision: b.displayPrecision ?? 2,
      roundLineItemsIndividually: b.roundLineItemsIndividually ?? false,
      // Por defecto apagada desde el 2026-11-03: hasta entonces el default
      // era `true` y prometía un paso de propina que no existe todavía.
      tippingEnabled: b.tippingEnabled ?? false,
      suggestedTips: Array.isArray(b.suggestedTips) ? b.suggestedTips : [5, 10, 15],
    },
    guestRoomChargeEnabled: b.guestRoomChargeEnabled ?? true,
    guestPrepaidEnabled: b.guestPrepaidEnabled ?? false,
    guestPaymentModeDefault: b.guestPaymentModeDefault ?? 'room_charge',
    guestPayment: {
      roomChargeEnabled: b.guestRoomChargeEnabled ?? true,
      prepaidEnabled: b.guestPrepaidEnabled ?? false,
      paymentModeDefault: b.guestPaymentModeDefault ?? 'room_charge',
      defaultDepositPct: b.defaultDepositPct ?? 30,
      balanceDueWindow: b.balanceDueWindow ?? '14_days_before',
      cancellationPolicyText: b.cancellationPolicyText ?? '',
      folioChargeFnb: b.folioChargeFnb ?? true,
      folioChargeActivities: b.folioChargeActivities ?? true,
      folioChargeShop: b.folioChargeShop ?? false,
      folioCapPerStay:
        b.folioCapPerStay !== undefined ? Number(b.folioCapPerStay) : 0,
      allowedPaymentMethods:
        Array.isArray(b.allowedPaymentMethods) && b.allowedPaymentMethods.length > 0
          ? (b.allowedPaymentMethods as Array<'cash' | 'card' | 'bank_transfer' | 'guest_prepaid' | 'room_charge'>)
          : ['cash', 'card', 'bank_transfer', 'room_charge'],
      prepaidMinRecharge:
        b.prepaidMinRecharge !== undefined ? Number(b.prepaidMinRecharge) : 20,
      prepaidMaxBalance:
        b.prepaidMaxBalance !== undefined ? Number(b.prepaidMaxBalance) : 500,
      prepaidDefaultTopups: Array.isArray(b.prepaidDefaultTopups)
        ? b.prepaidDefaultTopups
        : [20, 50, 100, 200],
      prepaidRefundPolicy: b.prepaidRefundPolicy ?? 'auto_on_checkout',
      prepaidBonusTiers: Array.isArray(b.prepaidBonusTiers)
        ? b.prepaidBonusTiers.map((tier) => ({
            threshold: Number(tier.threshold),
            bonus: Number(tier.bonus),
          }))
        : [],
      prepaidRedemptionSurfaces:
        Array.isArray(b.prepaidRedemptionSurfaces) && b.prepaidRedemptionSurfaces.length > 0
          ? (b.prepaidRedemptionSurfaces as Array<'restaurant_bar' | 'shop' | 'activities' | 'spa'>)
          : ['restaurant_bar', 'shop', 'activities'],
    },
    staffDefaults: {
      minRestBetweenShiftsHours: b.minRestBetweenShiftsHours ?? 11,
      autoApproveShiftCoverage: b.autoApproveShiftCoverage ?? false,
      activityCancelAfterStartRequiresManager: b.activityCancelAfterStartRequiresManager ?? true,
      folioCapOverrideRequiresManager: b.folioCapOverrideRequiresManager ?? true,
    },
    crewApp: {
      nfcAttendanceEnabled: b.crewNfcAttendanceEnabled ?? true,
      manualAttendanceEnabled: b.crewManualAttendanceEnabled ?? true,
      activityVisibility: (b.crewActivityVisibility as CrewActivityVisibility) ?? 'location',
      shiftSwapEnabled: b.crewShiftSwapEnabled ?? true,
    },
    createdAt: new Date(b.createdAt),
    updatedAt: new Date(b.updatedAt || b.createdAt),
  };
}

/**
 * LocationService
 * 
 * Service for managing Location entities.
 */
export const LocationService = {
  async getAllStrict(filters?: LocationFilters): Promise<Location[]> {
    const params: Record<string, string> = {};
    if (filters?.search) params.search = filters.search;

    const response = await apiClient.get<{ results: BackendLocation[]; count: number }>(
      '/api/v1/locations/',
      { params, showErrorToast: false, skipLocationFilter: true }
    );

    return response.data.results.map(mapBackendLocation);
  },

  async getAll(filters?: LocationFilters): Promise<Location[]> {
    return this.getAllStrict(filters);
  },

  /**
   * Get location summaries (lightweight list)
   */
  async getSummaries(filters?: LocationFilters): Promise<LocationSummary[]> {
    const locations = await this.getAll(filters);
    return locations.map((loc) => ({
      id: loc.id,
      name: loc.name,
      slug: loc.slug,
      type: loc.type,
      status: loc.status,
      city: loc.address.city,
      country: loc.address.country,
    }));
  },

  /**
   * Get a single location by ID
   */
  async getByIdStrict(id: string): Promise<Location | null> {
    const response = await apiClient.get<BackendLocation>(`/api/v1/locations/${id}/`, {
      skipLocationFilter: true,
      showErrorToast: false,
    });
    return mapBackendLocation(response.data);
  },

  async getById(id: string): Promise<Location | null> {
    try {
      return await this.getByIdStrict(id);
    } catch (error) {
      if ((error as { code?: string }).code === 'NOT_FOUND') {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get a single location by slug
   */
  async getBySlug(slug: string): Promise<Location | null> {
    const locations = await this.getAllStrict({ search: slug });
    return locations.find((loc) => loc.slug === slug) || null;
  },

  /**
   * Get location with billing profiles
   */
  async getWithBillingProfiles(id: string): Promise<LocationWithBillingProfiles | null> {
    const { BillingProfileService } = await import('./billingProfileService');
    const [location, billingProfiles] = await Promise.all([
      this.getById(id),
      BillingProfileService.getByLocationId(id),
    ]);
    if (!location) return null;
    return { ...location, billingProfiles };
  },

  /**
   * Create a new location.
   *
   * When `input.setup` is provided, the backend composes
   * copy-categories + copy-staff-positions + grant-initial-access in
   * the same atomic transaction as the location row. The wizard reads
   * the resulting `setupReport` to surface per-block counts and any
   * password-setup token (when a new admin user was created).
   */
  async create(
    input: LocationInput,
  ): Promise<Location & { setupReport?: LocationSetupReport }> {
    const payload: Record<string, unknown> = {
      name: input.name,
      type: input.type || 'surfcamp',
      description: input.description || '',
      operational_status: input.operationalStatus || 'active',
      brand_color: input.brandColor || '',
      address: input.address?.street || '',
      city: input.address?.city || '',
      state: input.address?.state || '',
      country: input.address?.country || '',
      postal_code: input.address?.postalCode || '',
      phone: input.contact?.phone || '',
      email: input.contact?.email || '',
      website: input.contact?.website || '',
      contact_config: buildContactConfig(input.contact),
      logo_url: input.logoUrl || '',
      timezone: input.monetary?.timezone || 'UTC',
      currency: input.monetary?.currencies?.find(c => c.isDefault)?.code || 'EUR',
      currencies: input.monetary?.currencies || [],
      locale: input.locale || 'en-US',
      active_modules: input.activeModules?.length ? Array.from(new Set(['core_stay', ...input.activeModules])) : ['core_stay'],
    };
    if (input.setup) {
      // The setup block stays on the camelCased shape — apiClient's
      // CamelCaseJSON middleware converts it to snake_case server-side.
      payload.setup = input.setup;
    }
    const response = await apiClient.post<BackendLocation & { setupReport?: LocationSetupReport }>(
      '/api/v1/locations/',
      payload,
    );
    const mapped = mapBackendLocation(response.data);
    return { ...mapped, setupReport: response.data.setupReport };
  },

  /**
   * Update an existing location
   */
  async update(input: LocationUpdateInput): Promise<Location> {
    const { id, ...rest } = input;
    const payload: Record<string, unknown> = {};
    if (rest.name !== undefined)                     payload.name = rest.name;
    if (rest.type !== undefined)                     payload.type = rest.type;
    if (rest.status !== undefined)                   payload.is_active = rest.status === 'active';
    if (rest.operationalStatus !== undefined)        payload.operational_status = rest.operationalStatus;
    if (rest.description !== undefined)              payload.description = rest.description;
    if (rest.brandColor !== undefined)               payload.brand_color = rest.brandColor;
    if (rest.weekStartDay !== undefined)             payload.week_start_day = rest.weekStartDay;
    if (rest.weekendDays !== undefined)              payload.weekend_days = rest.weekendDays;
    if (rest.defaultCheckInTime !== undefined)       payload.default_check_in_time = rest.defaultCheckInTime;
    if (rest.defaultCheckOutTime !== undefined)      payload.default_check_out_time = rest.defaultCheckOutTime;
    if (rest.dateFormat !== undefined)               payload.date_format = rest.dateFormat;
    if (rest.timeFormat !== undefined)               payload.time_format = rest.timeFormat;
    if (rest.numberFormat !== undefined)             payload.number_format = rest.numberFormat;
    if (rest.unitSystem !== undefined)               payload.unit_system = rest.unitSystem;
    if (rest.capacity?.totalRooms !== undefined)               payload.total_rooms = rest.capacity.totalRooms;
    if (rest.capacity?.totalBeds !== undefined)                payload.total_beds = rest.capacity.totalBeds;
    if (rest.capacity?.maxGuests !== undefined)                payload.max_guests = rest.capacity.maxGuests;
    if (rest.capacity?.maxPerRoom !== undefined)               payload.max_per_room = rest.capacity.maxPerRoom;
    if (rest.capacity?.occupancyWarningPct !== undefined)      payload.occupancy_warning_pct = rest.capacity.occupancyWarningPct;
    if (rest.capacity?.occupancyCriticalPct !== undefined)     payload.occupancy_critical_pct = rest.capacity.occupancyCriticalPct;
    if (rest.capacity?.blockOvercapacityBookings !== undefined) payload.block_overcapacity_bookings = rest.capacity.blockOvercapacityBookings;
    if (rest.guestFormRequiredFields !== undefined) payload.guest_form_required_fields = rest.guestFormRequiredFields;
    if (rest.capacity?.allowExtraGuestBeds !== undefined)      payload.allow_extra_guest_beds = rest.capacity.allowExtraGuestBeds;
    if (rest.capacity?.minStayNights !== undefined)            payload.min_stay_nights = rest.capacity.minStayNights;
    if (rest.capacity?.maxStayNights !== undefined)            payload.max_stay_nights = rest.capacity.maxStayNights;
    if (rest.capacity?.defaultStayNights !== undefined)        payload.default_stay_nights = rest.capacity.defaultStayNights;
    if (rest.capacity?.bookingWindowDays !== undefined)        payload.booking_window_days = rest.capacity.bookingWindowDays;
    if (rest.capacity?.maxPerBooking !== undefined)            payload.max_per_booking = rest.capacity.maxPerBooking;
    if (rest.capacity?.groupThreshold !== undefined)           payload.group_threshold = rest.capacity.groupThreshold;
    if (rest.capacity?.groupRequiresApproval !== undefined)    payload.group_requires_approval = rest.capacity.groupRequiresApproval;
    if (rest.address?.street !== undefined)          payload.address = rest.address.street;
    if (rest.address?.city !== undefined)            payload.city = rest.address.city;
    if (rest.address?.state !== undefined)           payload.state = rest.address.state;
    if (rest.address?.country !== undefined)         payload.country = rest.address.country;
    if (rest.address?.postalCode !== undefined)      payload.postal_code = rest.address.postalCode;
    if (rest.contact?.phone !== undefined)           payload.phone = rest.contact.phone;
    if (rest.contact?.email !== undefined)           payload.email = rest.contact.email;
    if (rest.contact?.website !== undefined)         payload.website = rest.contact.website;
    if (
      rest.contact?.phoneSecondary !== undefined ||
      rest.contact?.socialMedia !== undefined ||
      rest.contact?.departmentContacts !== undefined
    ) {
      payload.contact_config = buildContactConfig(rest.contact);
    }
    if (rest.logoUrl !== undefined)                  payload.logo_url = rest.logoUrl;
    if (rest.monetary?.timezone !== undefined)       payload.timezone = rest.monetary.timezone;
    if (rest.monetary?.currencies?.length) {
      const defaultCur = rest.monetary.currencies.find((c) => c.isDefault) ?? rest.monetary.currencies[0];
      payload.currency = defaultCur.code;
      payload.currencies = rest.monetary.currencies;
    }
    if (rest.monetary?.showPricesInGuestCurrency !== undefined) payload.show_prices_in_guest_currency = rest.monetary.showPricesInGuestCurrency;
    if (rest.monetary?.taxLabel !== undefined)                  payload.tax_label = rest.monetary.taxLabel;
    if (rest.monetary?.taxDisplayMode !== undefined)            payload.tax_display_mode = rest.monetary.taxDisplayMode;
    if (rest.monetary?.cashRounding !== undefined)              payload.cash_rounding = rest.monetary.cashRounding;
    if (rest.monetary?.displayPrecision !== undefined)          payload.display_precision = rest.monetary.displayPrecision;
    if (rest.monetary?.roundLineItemsIndividually !== undefined) payload.round_line_items_individually = rest.monetary.roundLineItemsIndividually;
    if (rest.monetary?.tippingEnabled !== undefined)            payload.tipping_enabled = rest.monetary.tippingEnabled;
    if (rest.monetary?.suggestedTips !== undefined)             payload.suggested_tips = rest.monetary.suggestedTips;
    if (rest.staffDefaults) {
      const sd = rest.staffDefaults;
      if (sd.minRestBetweenShiftsHours !== undefined)      payload.min_rest_between_shifts_hours = sd.minRestBetweenShiftsHours;
      if (sd.autoApproveShiftCoverage !== undefined)       payload.auto_approve_shift_coverage = sd.autoApproveShiftCoverage;
      if (sd.activityCancelAfterStartRequiresManager !== undefined)
        payload.activity_cancel_after_start_requires_manager = sd.activityCancelAfterStartRequiresManager;
      if (sd.folioCapOverrideRequiresManager !== undefined)
        payload.folio_cap_override_requires_manager = sd.folioCapOverrideRequiresManager;
    }
    if (rest.crewApp) {
      const ca = rest.crewApp;
      if (ca.nfcAttendanceEnabled !== undefined)    payload.crew_nfc_attendance_enabled = ca.nfcAttendanceEnabled;
      if (ca.manualAttendanceEnabled !== undefined) payload.crew_manual_attendance_enabled = ca.manualAttendanceEnabled;
      if (ca.activityVisibility !== undefined)      payload.crew_activity_visibility = ca.activityVisibility;
      if (ca.shiftSwapEnabled !== undefined)        payload.crew_shift_swap_enabled = ca.shiftSwapEnabled;
    }
    if (rest.guestPayment) {
      const gp = rest.guestPayment;
      if (gp.roomChargeEnabled !== undefined)         payload.guest_room_charge_enabled = gp.roomChargeEnabled;
      if (gp.prepaidEnabled !== undefined)            payload.guest_prepaid_enabled = gp.prepaidEnabled;
      if (gp.paymentModeDefault !== undefined)        payload.guest_payment_mode_default = gp.paymentModeDefault;
      if (gp.defaultDepositPct !== undefined)         payload.default_deposit_pct = gp.defaultDepositPct;
      if (gp.balanceDueWindow !== undefined)          payload.balance_due_window = gp.balanceDueWindow;
      if (gp.cancellationPolicyText !== undefined)    payload.cancellation_policy_text = gp.cancellationPolicyText;
      if (gp.folioChargeFnb !== undefined)            payload.folio_charge_fnb = gp.folioChargeFnb;
      if (gp.folioChargeActivities !== undefined)     payload.folio_charge_activities = gp.folioChargeActivities;
      if (gp.folioChargeShop !== undefined)           payload.folio_charge_shop = gp.folioChargeShop;
      if (gp.folioCapPerStay !== undefined)           payload.folio_cap_per_stay = gp.folioCapPerStay;
      if (gp.allowedPaymentMethods !== undefined)     payload.allowed_payment_methods = gp.allowedPaymentMethods;
      if (gp.prepaidMinRecharge !== undefined)        payload.prepaid_min_recharge = gp.prepaidMinRecharge;
      if (gp.prepaidMaxBalance !== undefined)         payload.prepaid_max_balance = gp.prepaidMaxBalance;
      if (gp.prepaidDefaultTopups !== undefined)      payload.prepaid_default_topups = gp.prepaidDefaultTopups;
      if (gp.prepaidRefundPolicy !== undefined)       payload.prepaid_refund_policy = gp.prepaidRefundPolicy;
      if (gp.prepaidBonusTiers !== undefined)         payload.prepaid_bonus_tiers = gp.prepaidBonusTiers;
      if (gp.prepaidRedemptionSurfaces !== undefined) payload.prepaid_redemption_surfaces = gp.prepaidRedemptionSurfaces;
    }
    if (rest.locale !== undefined)                   payload.locale = rest.locale;
    if (rest.activeModules !== undefined)            payload.active_modules = Array.from(new Set(['core_stay', ...rest.activeModules]));

    const response = await apiClient.patch<BackendLocation>(`/api/v1/locations/${id}/`, payload);
    return mapBackendLocation(response.data);
  },

  /**
   * Delete a location (soft-delete on backend: marks is_active=false)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/locations/${id}/`);
  },

  /**
   * Update location status
   */
  async updateStatus(id: string, status: Location['status']): Promise<Location> {
    return this.update({ id, status });
  },

  /**
   * Generate URL-friendly slug from name
   */
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  },

  /**
   * Check if slug is available
   */
  async isSlugAvailable(slug: string, excludeId?: string): Promise<boolean> {
    const locations = await this.getAllStrict({ search: slug });
    const existing = locations.find((loc) => loc.slug === slug);
    if (!existing) return true;
    return excludeId ? existing.id === excludeId : false;
  },
};

export default LocationService;
